import {
  Link,
  createFileRoute,
  useNavigate,
  useRouterState,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'

import { TitleRowSkeleton } from '@/components/layout/PendingPage'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import {
  reviewSubmitFailure,
  useBrokerDetail,
  useBrokerReviews,
  useSubmitBrokerReview,
} from '@/features/brokers/api/queries'
import { useAuthStore } from '@/lib/auth/store'
import { brokerDisplayName, brokerShortName, type ReviewDraft } from '@/schemas/broker'
import { pageSizeSchema } from '@/schemas/table'
import { loadBrokerReviews } from '@/screens/brokers/BrokerReviewsScreen.loader'
import { brokerReviewsSearchSchema, toBrokerReviewsQuery } from '@/screens/brokers/search'
import RatingSummary from '@/screens/brokers/components/reviews/RatingSummary'
import ReviewComposer from '@/screens/brokers/components/reviews/ReviewComposer'
import ReviewList from '@/screens/brokers/components/reviews/ReviewList'
import {
  REVIEWS_COLUMNS,
  ReviewsSkeleton,
} from '@/screens/brokers/components/reviews/ReviewsStates'

/**
 * The mock's visible title and back control (owner ruling 5, 2026-10-28): a small boxed chevron
 * back to By broker, then "NIBL Ace reviews" at the page-title size. This page is the one strip
 * page that keeps its title, and the house is changed from By broker, not from a picker here.
 */
function ReviewsTitle({ code, title }: { code: string; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <Link
        to="/brokers/$code"
        params={{ code }}
        aria-label="Back to this broker"
        className="group/back inline-flex size-5 shrink-0 items-center justify-center bg-muted text-muted-foreground inset-ring inset-ring-border transition-[box-shadow,translate,color] duration-fast hover:-translate-x-px hover:text-foreground hover:inset-ring-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <ChevronLeft className="size-3" aria-hidden="true" />
      </Link>
      <h1 className="text-h-2xl text-foreground">{title}</h1>
    </div>
  )
}

function ReviewsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading these reviews…" header={<TitleRowSkeleton />}>
      <ReviewsSkeleton />
    </ScreenLayout>
  )
}

/**
 * Light on purpose (docs/route-loading.md, "Error components"): only
 * `RouteErrorPage` plus the title's skeleton, so it stays in the entry
 * chunk. `BROKER_NOT_FOUND` never
 * reaches here any more — `brokerReviewsQueryOptions` resolves it to
 * `data === null` instead of throwing, and `RatingSummary`/`ReviewList`
 * already draw their own not-found copy for that (below). This component
 * only ever shows for a genuine failure: offline, or the request itself
 * broke.
 */
function ReviewsError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage {...props} header={<TitleRowSkeleton />} title="Couldn't load these reviews" />
  )
}

export const Route = createFileRoute('/_app/brokers/$code/reviews')({
  validateSearch: brokerReviewsSearchSchema,
  loader: loadBrokerReviews,
  pendingComponent: ReviewsPending,
  errorComponent: ReviewsError,
  component: BrokerReviewsScreen,
})

/**
 * Brokers · Reviews: one house's rating summary and its members' reviews.
 *
 * A sibling route of `/brokers/$code`, not a tab inside it, and a tab of the section strip of its
 * own, reached param-less through `/brokers/reviews` (`ReviewsRedirect`). Unlike the other strip
 * pages it keeps a visible title, "NIBL Ace reviews", and a back control to By broker, the
 * mock's (owner ruling 5, 2026-10-28); the house is chosen on By broker, not by a picker here.
 * The rating summary and the reviews sit side by side, the summary a 320px column, once the main
 * column is wide enough for both (`REVIEWS_COLUMNS`), and stack below that. The member's own
 * review is written in an inline card at the top of the list.
 *
 * ── Read and write are different questions ───────────────────────────────────
 * The GET is PUBLIC. Everything on this page — the average, the histogram, the
 * cards — renders for a visitor with no session, and nothing on the read path
 * asks whether there is one. Only the POST is authenticated, so the session is
 * consulted in exactly two places: which button the rail draws, and what to do
 * when a submission comes back `signInRequired`.
 *
 * The sign-in hand-off is the app's existing one and not a new pattern: there
 * is no auth sheet in this codebase — `lib/auth/guards.ts` and
 * `routes/layouts/app.guard.ts` both send a member to `/signin` carrying the
 * href to come back to, and `SignInForm` navigates to that href on success. So
 * that is what the rail's button and the composer's Sign in action do, and the
 * draft is still on screen when they land back here.
 */
function BrokerReviewsScreen() {
  const { code } = Route.useParams()
  const query = Route.useSearch()
  const navigate = useNavigate()
  const href = useRouterState({ select: (state) => state.location.href })

  const [composerOpen, setComposerOpen] = useState(false)
  // The star the histogram filtered the list to, and whether this page has posted a review (the
  // mock's "Thanks for your review"); both are this visit's, not the URL's.
  const [selectedStar, setSelectedStar] = useState<number | null>(null)
  const [posted, setPosted] = useState(false)
  // Bumped on every accepted review, so the composer remounts with an empty
  // draft next time rather than reopening on the words just posted.
  const [draftGeneration, setDraftGeneration] = useState(0)

  const isSignedIn = useAuthStore((state) => state.status === 'authenticated')

  // Through `toBrokerReviewsQuery`, not `query` itself — see the picker's
  // own comment in search.ts for why the two are not interchangeable.
  const reviews = useBrokerReviews(code, toBrokerReviewsQuery(query))
  // Only for the house's name in the title and the picker. Same key the By
  // broker page filled, so arriving through the "132 reviews ›" link costs no
  // extra request.
  const detail = useBrokerDetail(code, 'today')
  const mutation = useSubmitBrokerReview(code)

  // `reviews.data === null` means BROKER_NOT_FOUND, caught in
  // `brokerReviewsQueryOptions`'s own `queryFn` rather than thrown
  // (docs/route-loading.md, "Not-found answers") — the same shape Stock's
  // `company === null` uses.
  const notFound = reviews.data === null
  const brokerName = detail.data?.header.name ?? brokerDisplayName(code, null)

  const goToSignIn = () => {
    void navigate({ to: '/signin', search: { redirect: href } })
  }

  const submit = (draft: ReviewDraft) => {
    mutation.mutate(draft, {
      onSuccess: () => {
        // Success already invalidates reviewsFor(code) and detailFor(code) in
        // the hook — the header rating is the same average — so there is
        // nothing to invalidate here.
        setComposerOpen(false)
        setPosted(true)
        setDraftGeneration((generation) => generation + 1)
      },
    })
  }

  const changePage = (page: number) => {
    void navigate({ to: '.', search: (prev) => ({ ...prev, page }) })
  }

  const shortName = brokerShortName(brokerName)
  const title = `${shortName} reviews`

  return (
    <ScreenLayout
      header={<ReviewsTitle code={code} title={title} />}
      pageTitle={`${shortName} Reviews`}
    >
      <div className={REVIEWS_COLUMNS}>
        <RatingSummary
          summary={reviews.data ?? undefined}
          isLoading={reviews.isPending}
          isError={reviews.isError}
          notFound={notFound}
          onRetry={() => {
            void reviews.refetch()
          }}
          isSignedIn={isSignedIn}
          posted={posted}
          selectedStar={selectedStar}
          onSelectStar={setSelectedStar}
          onWriteReview={() => {
            setComposerOpen(true)
          }}
          onSignIn={goToSignIn}
        />

        <div className="flex min-w-0 flex-col gap-4">
          <ReviewComposer
            key={draftGeneration}
            open={composerOpen}
            onOpenChange={setComposerOpen}
            brokerName={shortName}
            isPending={mutation.isPending}
            failure={reviewSubmitFailure(mutation.error)}
            onSubmit={submit}
            onSignIn={goToSignIn}
          />

          <ReviewList
            summary={reviews.data ?? undefined}
            pageSize={query.pageSize}
            onPageChange={changePage}
            onPageSizeChange={(pageSize) => {
              const parsed = pageSizeSchema.safeParse(pageSize)
              if (!parsed.success) return
              void navigate({
                to: '.',
                search: (prev) => ({ ...prev, pageSize: parsed.data, page: 1 }),
              })
            }}
            isLoading={reviews.isPending}
            isError={reviews.isError}
            notFound={notFound}
            onRetry={() => {
              void reviews.refetch()
            }}
            selectedStar={selectedStar}
            onClearStar={() => {
              setSelectedStar(null)
            }}
          />
        </div>
      </div>
    </ScreenLayout>
  )
}
